export async function waitForSessionKey({ extractable = false } = {}) {
  const usages = ["encrypt", "decrypt"];
  const algorithm = { name: "AES-GCM" };

  if (window.sessionSharedKey instanceof CryptoKey) {
    if (extractable && !window.sessionSharedKey.extractable) {
      const rawB64 = window.sessionSharedKeyRawB64;
      if (typeof rawB64 === "string" && rawB64.length > 0) {
        const raw = Uint8Array.from(atob(rawB64), c => c.charCodeAt(0));
        return importKey(raw, algorithm, true, usages);
      }
    }

    return window.sessionSharedKey;
  }

  if (typeof window.sessionSharedKey === "string") {
    const raw = Uint8Array.from(atob(window.sessionSharedKey), c => c.charCodeAt(0));
    return importKey(raw, algorithm, extractable, usages);
  }

  // Handle Uint8Array or ArrayBuffer
  if (window.sessionSharedKey instanceof ArrayBuffer || ArrayBuffer.isView(window.sessionSharedKey)) {
    const raw = window.sessionSharedKey instanceof ArrayBuffer
      ? new Uint8Array(window.sessionSharedKey)
      : new Uint8Array(window.sessionSharedKey.buffer);
    return importKey(raw, algorithm, extractable, usages);
  }

  // Wait for "sessionKeyReady" event with timeout
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("⏳ Timed out waiting for session key"));
    }, 5000);

    window.addEventListener("sessionKeyReady", async () => {
      clearTimeout(timeout);
      try {
        const key = await waitForSessionKey({ extractable });
        resolve(key);
      } catch (err) {
        reject(err);
      }
    }, { once: true });
  });
}

/**
 * Imports a raw AES-GCM key.
 */
async function importKey(rawKey, algorithm, extractable, usages) {
  const key = await crypto.subtle.importKey("raw", rawKey, algorithm, extractable, usages);
  window.sessionSharedKeyRawB64 = btoa(String.fromCharCode(...rawKey));
  window.sessionSharedKey = key;
  return key;
}

/**
 * Decrypts an encrypted text bubble using AES-GCM and the shared session key.
 */
export async function decryptBubble(encryptedTextB64, ivB64) {
  try {
    const key = await waitForSessionKey();
    if (!key) {
      console.error("🚨 No session key available for decryption.");
      return null;
    }

    const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
    const encryptedAndTag = Uint8Array.from(atob(encryptedTextB64), c => c.charCodeAt(0));

    console.log("🔎 Decrypting...");
    console.log("🔐 Key type:", key.constructor?.name);
    console.log("📦 Base64 IV:", ivB64, "→", iv);
    console.log("📦 Base64 Ciphertext:", encryptedTextB64.slice(0, 30) + "...");

    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      key,
      encryptedAndTag
    );

    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    console.error("🧨 Decryption failed:", error);
    return null;
  }
}
